import { useTranslation } from '../../i18n/useTranslation'
import { useTheme } from '../../hooks/useTheme'
import { useAuth } from '../../contexts/AuthContext'
import { entitlementsOf } from '../../utils/entitlements'
import { Button } from '../ui'

interface CompleteSetModalProps {
  isOpen: boolean
  isCompleting: boolean
  autoProlongation: boolean
  onAutoProlongationChange: (value: boolean) => void
  onConfirm: () => void
  onCancel: () => void
}

export function CompleteSetModal({
  isOpen,
  isCompleting,
  autoProlongation,
  onAutoProlongationChange,
  onConfirm,
  onCancel
}: CompleteSetModalProps) {
  const { t, translate } = useTranslation()
  const theme = useTheme()
  const { user } = useAuth()
  const hasAutoProlongation = entitlementsOf(user).autoProlongation
  
  if (!isOpen) return null

  return (
    <div className="fixed inset-0 flex items-center justify-center p-4 z-50" style={{ backgroundColor: 'rgba(0, 0, 0, 0.5)' }}>
      <div 
        className="rounded-2xl p-6 max-w-md w-full"
        style={{
          backgroundColor: theme.bgColor,
          color: theme.textColor,
        }}
      >
        <h3 className="text-xl font-bold mb-4">{t.modal.complete.title}</h3>
        
        <p className="text-sm opacity-70 mb-4">
          {t.modal.complete.description}
        </p>

        <label 
          className="flex items-start gap-3 mb-6 cursor-pointer p-3 rounded-lg"
          style={{
            backgroundColor: theme.cardBg,
            border: `1px solid ${theme.cardBorder}`,
            opacity: hasAutoProlongation ? 1 : 0.6,
            cursor: hasAutoProlongation ? 'pointer' : 'not-allowed',
          }}
        >
          <input
            type="checkbox"
            checked={autoProlongation}
            onChange={(e) => hasAutoProlongation && onAutoProlongationChange(e.target.checked)}
            disabled={!hasAutoProlongation}
            className="mt-1 w-5 h-5 rounded disabled:cursor-not-allowed"
            style={{
              accentColor: 'rgb(0, 136, 204)',
            }}
          />
          <div className="flex-1">
            <div className="font-semibold mb-1">{t.modal.complete.autoProlongation.label}</div>
            <div className="text-sm opacity-70">
              {hasAutoProlongation 
                ? t.modal.complete.autoProlongation.description
                : translate('subscription.feature.autoProlongation.upgradeRequired')}
            </div>
          </div>
        </label>

        <div className="flex gap-3">
          <Button
            variant="secondary"
            size="md"
            onClick={onCancel}
            disabled={isCompleting}
            fullWidth
          >
            {t.modal.complete.cancel}
          </Button>
          <Button
            variant="success"
            size="md"
            onClick={onConfirm}
            disabled={isCompleting}
            fullWidth
          >
            {isCompleting ? t.trainingSetActions.completing : t.modal.complete.confirm}
          </Button>
        </div>
      </div>
    </div>
  )
}
